import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Phone, Mail } from "lucide-react";
import { Layout } from "@/components/Layout";
import { fetchVehicle } from "@/lib/api";
import { formatMiles, formatPrice, COMPANY } from "@/lib/company";

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between py-2.5 px-4 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900 text-right">{value}</span>
  </div>
);

export default function VehicleDetail() {
  const { stock } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    setV(null); setError(false); setActive(0);
    fetchVehicle(stock).then(setV).catch(() => setError(true));
  }, [stock]);

  if (error) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Vehicle not found</h1>
          <p className="mt-2 text-slate-600">This vehicle may no longer be available.</p>
          <Link to="/catalog" className="mt-6 inline-flex rounded-md bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white">Back to Catalog</Link>
        </div>
      </Layout>
    );
  }

  if (!v) {
    return <Layout><div className="mx-auto max-w-7xl px-4 py-24 text-slate-500">Loading vehicle…</div></Layout>;
  }

  const sold = v.availability !== "Available";
  const specs = [
    ["Stock Number", `#${v.stock_number}`],
    ["Year", v.year],
    ["Make", v.make],
    ["Model", v.model],
    ["Body Style", v.body_style],
    ["Mileage", formatMiles(v.mileage)],
    ["Exterior Color", v.exterior_color],
    ["Interior Color", v.interior_color],
    ["Engine", v.engine],
    ["Transmission", v.transmission],
    ["Drivetrain", v.drivetrain],
    ["Fuel Type", v.fuel_type],
    ["Condition", v.condition],
  ];

  const inquire = (type) =>
    navigate(`/contact?type=${encodeURIComponent(type)}&stock=${v.stock_number}`);

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-6">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-800" data-testid="detail-back-link">
          <ChevronLeft className="h-4 w-4" /> Back to Catalog
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-16 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        <div>
          <div className="aspect-[16/10] bg-slate-100 rounded-md overflow-hidden border border-slate-200">
            <img src={v.images[active]} alt={`${v.year} ${v.make} ${v.model}`} className="h-full w-full object-cover" data-testid="detail-main-image" />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-3" data-testid="detail-thumbnails">
            {v.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`aspect-[4/3] rounded-md overflow-hidden border-2 ${active === i ? "border-blue-700" : "border-slate-200 hover:border-slate-400"}`}
                data-testid={`detail-thumb-${i}`}
              >
                <img src={img} alt={`View ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900">Vehicle Specifications</h2>
            <div className="mt-3 border border-slate-200 rounded-md divide-y divide-slate-200 bg-white" data-testid="detail-specs">
              {specs.map(([label, value]) => <SpecRow key={label} label={label} value={value} />)}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900">Description</h2>
            <p className="mt-3 text-slate-700 leading-relaxed">{v.description}</p>
          </div>
        </div>

        {/* Sticky sidebar */}
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <div className="text-xs font-mono text-slate-500">Stock #{v.stock_number}</div>
            <h1 className="mt-1 text-2xl font-extrabold text-slate-900 leading-tight">
              {v.year} {v.make} {v.model}
            </h1>
            <div className="mt-3 text-3xl font-extrabold text-blue-800">{formatPrice(v.price)}</div>

            <div className="mt-2">
              <span className={`inline-block text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded ${sold ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                {v.availability}
              </span>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm text-slate-600">
              <div><dt className="text-slate-400 text-xs">Mileage</dt><dd>{formatMiles(v.mileage)}</dd></div>
              <div><dt className="text-slate-400 text-xs">Fuel</dt><dd>{v.fuel_type}</dd></div>
              <div><dt className="text-slate-400 text-xs">Transmission</dt><dd>{v.transmission}</dd></div>
              <div><dt className="text-slate-400 text-xs">Drivetrain</dt><dd>{v.drivetrain}</dd></div>
            </dl>

            <div className="mt-5 space-y-3">
              <button onClick={() => inquire("Vehicle Purchase Inquiry")} data-testid="detail-contact-vehicle-btn" className="w-full rounded-md bg-blue-800 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-900">
                Contact About This Vehicle
              </button>
              <button onClick={() => inquire("General Inquiry")} data-testid="detail-more-info-btn" className="w-full rounded-md border border-blue-800 px-5 py-3 text-sm font-semibold text-blue-800 hover:bg-blue-50">
                Request More Information
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-200 space-y-2 text-sm">
              <a href={`tel:${COMPANY.phoneHref}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-800"><Phone className="h-4 w-4" /> {COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-800"><Mail className="h-4 w-4" /> {COMPANY.email}</a>
            </div>
          </div>

          <div className="mt-4 border border-slate-200 rounded-md p-5 bg-slate-50">
            <h3 className="font-semibold text-slate-900 text-sm">Need this vehicle shipped?</h3>
            <p className="mt-1 text-sm text-slate-600">We can transport this vehicle to your location. Send your details for a transport quote.</p>
            <button onClick={() => inquire("Transportation Quote")} data-testid="detail-transport-quote-btn" className="mt-3 text-sm font-semibold text-blue-800 hover:underline">
              Request a transport quote →
            </button>
          </div>
        </aside>
      </div>
    </Layout>
  );
}
